import { useCallback, useContext } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  AlertBannerContext,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  API_RESPONSE_CODE,
  dispatchMembershipContextRefresh
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import { authorizeQuery } from "../../Graphql/querries/authQuery";
import type { AuthorizeResponse } from "../../Graphql/types/auth";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

export type RefreshMembershipAfterPaymentInput = {
  /** From payment GraphQL response: true when proxy issued the featureToken cookie. */
  membershipTokenIssued?: boolean | null;
};

/**
 * After successful membership payment, refresh session membership context via authorize
 * (same flow as post-login) and notify the container app to update Auth/Membership state.
 *
 * The feature (membership) token cookie is httpOnly — it is set by the proxy Set-Cookie
 * header on the payment mutation response; the browser applies it via credentials: "include".
 */
export const useRefreshMembershipAfterPayment = () => {
  const { setAlert } = useContext(AlertBannerContext);

  const mutation = useMutation<
    AuthorizeResponse,
    Error,
    RefreshMembershipAfterPaymentInput | void
  >({
    mutationKey: ["refreshMembershipAfterPayment"],
    mutationFn: async () => {
      const response: AuthorizeResponse = await fetchGraphQL(
        authorizeQuery,
        { input: {} },
        setAlert
      );
      return response;
    }
  });

  const refreshMembershipAfterPayment = useCallback(
    async (input?: RefreshMembershipAfterPaymentInput): Promise<boolean> => {
      const tokenIssued = input?.membershipTokenIssued;

      if (tokenIssued === false) {
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Membership payment succeeded but membership token was not issued",
          buildLogContext("membership", "refresh-after-payment")
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.WARNING,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
      }

      try {
        const response = await mutation.mutateAsync(input);
        const { authorize } = response;
        const code = authorize?.code;
        const membership = authorize?.membership;

        if (code !== API_RESPONSE_CODE.OK || !authorize?.authorized) {
          sendAppLog(
            LOG_LEVEL.WARNING,
            "Authorize after membership payment did not return an active session",
            buildLogContext(
              "membership",
              "refresh-after-payment",
              undefined,
              undefined,
              undefined,
              {
                apiCode: String(code ?? "MISSING")
              }
            )
          );
          setAlert({
            severity: ALERT_BANNER_CODE_SEVERITY.WARNING,
            code: ALERT_BANNER_CODE.INTERNAL_ERROR
          });
          return false;
        }

        dispatchMembershipContextRefresh({
          membership: membership ?? "NONE",
          membershipTokenIssued: tokenIssued ?? undefined
        });

        sendAppLog(
          LOG_LEVEL.INFO,
          "Membership context refreshed after payment",
          buildLogContext(
            "membership",
            "refresh-after-payment",
            undefined,
            undefined,
            undefined,
            {
              membership: String(membership ?? "NONE"),
              membershipTokenIssued: String(tokenIssued ?? "unknown")
            }
          )
        );

        return membership === "ACTIVE";
      } catch (error) {
        captureAppException(
          "Failed to refresh membership context after payment",
          error,
          buildLogContext("membership", "refresh-after-payment")
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.WARNING,
          code: ALERT_BANNER_CODE.INTERNAL_ERROR
        });
        return false;
      }
    },
    [mutation, setAlert]
  );

  return {
    refreshMembershipAfterPayment,
    isRefreshing: mutation.isPending
  };
};
